import { useState } from "react";
import { Search } from "lucide-react";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface SearchResult {
  id: string;
  type: "job" | "candidate" | "client" | "company";
  title: string;
  subtitle?: string;
  path: string;
}

interface GlobalSearchProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GlobalSearch({ open, onOpenChange }: GlobalSearchProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleSearch = async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      return;
    }

    setSearching(true);
    try {
      const searchTerm = `%${searchQuery}%`;
      
      // Search jobs
      const { data: jobs } = await supabase
        .from("jobs")
        .select("id, title, location")
        .or(`title.ilike.${searchTerm},location.ilike.${searchTerm}`)
        .limit(5);

      // Search candidates
      const { data: candidates } = await supabase
        .from("candidates")
        .select("id, first_name, last_name, email_primary, city")
        .or(`first_name.ilike.${searchTerm},last_name.ilike.${searchTerm},email_primary.ilike.${searchTerm}`)
        .limit(5);

      // Search clients
      const { data: clients } = await supabase
        .from("clients")
        .select("id, name, company_name")
        .or(`name.ilike.${searchTerm},company_name.ilike.${searchTerm}`)
        .limit(5);

      // Search companies
      const { data: companies } = await supabase
        .from("companies")
        .select("id, name, location")
        .ilike("name", searchTerm)
        .limit(5);

      const combinedResults: SearchResult[] = [
        ...(jobs?.map(j => ({
          id: j.id,
          type: "job" as const,
          title: j.title,
          subtitle: j.location || "No location",
          path: `/recruiter/jobs/${j.id}`
        })) || []),
        ...(candidates?.map(c => ({
          id: c.id,
          type: "candidate" as const,
          title: `${c.first_name} ${c.last_name}`,
          subtitle: c.email_primary || c.city || "",
          path: `/recruiter/candidates/${c.id}`
        })) || []),
        ...(clients?.map(c => ({
          id: c.id,
          type: "client" as const,
          title: c.name,
          subtitle: c.company_name || "",
          path: `/recruiter/clients/${c.id}`
        })) || []),
        ...(companies?.map(c => ({
          id: c.id,
          type: "company" as const,
          title: c.name,
          subtitle: c.location || "",
          path: `/intelligence/companies/${c.id}`
        })) || [])
      ];

      setResults(combinedResults);
    } catch (error: any) {
      console.error("Search error:", error);
      toast({
        title: "Search failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSearching(false);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    onOpenChange(false);
    setQuery("");
    setResults([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Global Search</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search jobs, candidates, clients, companies..."
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                handleSearch(e.target.value);
              }}
              className="pl-9"
              autoFocus
            />
          </div>
          
          {searching && (
            <p className="text-sm text-muted-foreground text-center py-4">
              Searching...
            </p>
          )}

          {!searching && results.length > 0 && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {results.map((result) => (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full text-left p-3 hover:bg-accent rounded-lg transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium uppercase text-muted-foreground">
                      {result.type}
                    </span>
                    <div className="flex-1">
                      <p className="font-medium">{result.title}</p>
                      {result.subtitle && (
                        <p className="text-sm text-muted-foreground">
                          {result.subtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!searching && query.length >= 2 && results.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              No results found for "{query}"
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
